import type { JumpChart } from '@shared/jumpChart';
import type { JumpReplayInput, JumpReplayInputs, ReplayOutcome } from '@shared/replay';
import { step, type JumpEngineEvent, type JumpEngineInput } from '@shared/engine/jump/core';
import {
  buildJumpEngineConfig,
  createInitialJumpState,
  resetJumpStateForNextLoop,
  type JumpEngineConfig,
  type JumpEngineState,
} from '@shared/engine/jump/state';
import { computeAccuracy } from '@shared/scoring';
import { milestoneIsStar, milestoneTier, type MilestoneKind } from '@shared/progression';
import { AudioEngine, clampPlaybackRate } from '../audio/scheduler';
import { loadVolumePrefs } from '../audio/volumePrefs';
import { LoopRunState } from '../loopManager';
import { RunTimeline } from '../input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import { createEmptyJumpShake, JumpRenderer } from './jumpRenderer';
import {
  KeyboardJumpInputSource,
  ReplayJumpInputSource,
  type JumpInputSource,
} from './jumpInputSource';
import {
  CLOSE_CALL_CLEARANCE,
  CLOSE_CALL_COLOUR,
  COMBO_ENERGY_APPROACH_RATE,
  COMBO_ENERGY_FULL_AT,
  COMBO_MILESTONES,
  CUBE_EDGE_IMPULSE_DECAY,
  DEATH_COLOUR,
  FLOATING_TEXT_LIFE,
  FLOATING_TEXT_LIFE_BIG,
  GROUND_RIPPLE_FLATTEN,
  HIT_STOP_MS_CLOSE,
  HIT_STOP_MS_DEATH,
  HIT_STOP_MS_PASS_GOOD,
  HIT_STOP_MS_PASS_PERFECT,
  LAND_RIPPLE_ALPHA,
  LAND_RIPPLE_END_R,
  LAND_RIPPLE_LIFE,
  LAND_SHAKE_AMOUNT,
  MILESTONE_COLOUR,
  PASS_FLASH_INTENSITY_GOOD,
  PASS_FLASH_INTENSITY_PERFECT,
  PASS_PARTICLE_STRENGTH_GOOD,
  PASS_PARTICLE_STRENGTH_PERFECT,
  PASS_SHAKE_PERFECT,
  SCORE_POP_LIFE,
  VERDICT_COLOURS,
} from './render/jumpRenderConstants';
import type {
  JumpFlashSpec,
  JumpFloatingText,
  JumpParticleEmission,
  JumpScorePop,
  JumpShakeSpec,
  JumpShockwaveSpec,
} from './render/jumpRenderTypes';

export type JumpSessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

export interface JumpMilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface JumpGameSessionOptions {
  chart: JumpChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  /** Pre-recorded inputs (for replay verifier / debug). */
  replayInputs?: JumpEngineInput[];
  /** Optional human-readable key label shown in the HUD ("SPACE", "TAP"). */
  keyLabel?: string;
  onStatusChange?: (status: JumpSessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: JumpEngineEvent) => void;
  onMilestone?: (info: JumpMilestoneInfo) => void;
  testHook?: boolean;
  enableLoops?: boolean;
}

const LEAD_IN_BEATS = 4;
const MIN_LEAD_IN_WALL_SECONDS = 2.0;
/** Eased score lerp factor; same constant the other modes use for the HUD readout. */
const SCORE_DISPLAY_LERP_RATE = 15;

function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) {
    return baseBeats;
  }
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

/**
 * Jump-mode game session. Mirrors {@link import('../waves/wavesSession.js').WavesGameSession}'s
 * lifecycle (constructor → start() → tick loop → pause/resume → destroy) so the
 * Jump page parallels the other mode pages cleanly.
 *
 * Composition only — engine, renderer, audio, and input are dependency-injected,
 * never inherited. The session is the single "conductor" that pulls audio time
 * forward, polls inputs, steps the shared engine, fans events into renderer FX
 * queues, and feeds the renderer once per frame.
 *
 * Like Waves it's a one-shot-fail model — any collision ends the run, no HP bar
 * — and reuses LoopRunState verbatim so star/cup gates fire on the same beat
 * fractions as every other mode.
 */
export class JumpGameSession {
  private chart: JumpChart;
  private engineConfig: JumpEngineConfig;
  private state: JumpEngineState;
  private audio: AudioEngine;
  private renderer: JumpRenderer;
  private inputSource: JumpInputSource;
  /** Anti-cheat timing recorder (real-time progression + isTrusted). */
  private timeline: RunTimeline;
  private particleEmissions: JumpParticleEmission[] = [];
  private floatingTexts: JumpFloatingText[] = [];
  private scorePops: JumpScorePop[] = [];
  private flashes: JumpFlashSpec[] = [];
  private shockwaveSpecs: JumpShockwaveSpec[] = [];
  private shake: JumpShakeSpec = createEmptyJumpShake();
  private currentBeatPulse = 0;
  /** Smoothed 0..1 "in the zone" combo energy. Render-only; derived from `state.combo`. */
  private comboEnergy = 0;
  /** Signed cube deformation impulse: +1 on land (squash), −1 on jump (stretch), eased to 0. Render-only. */
  private edgeImpulse = 0;
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: JumpSessionStatus = 'idle';
  private opts: JumpGameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: JumpEngineEvent[] = [];
  private loopCapturedInputs: JumpReplayInput[][] = [[]];
  private comboMilestonesHit = new Set<number>();
  private lastTickAt = performance.now();
  private loopState: LoopRunState;
  private enableLoops: boolean;
  private destroyed = false;
  private autoPaused = false;
  private displayedScore = 0;
  private cachedRect = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;

  constructor(opts: JumpGameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildJumpEngineConfig(this.chart);
    this.state = createInitialJumpState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new JumpRenderer(opts.canvas);
    this.attachResizeObserver();

    if (opts.replayInputs) {
      this.inputSource = new ReplayJumpInputSource(opts.replayInputs);
    } else {
      this.inputSource = new KeyboardJumpInputSource(
        () => this.audio.getCurrentBeat(),
        // Only feed the engine once the run is LIVE. During the count-in the
        // source still tracks isPressing() (for the cube's press-swell cue) but
        // queues nothing, so an eager tap/spam-click that's RELEASED before the
        // run begins never pre-loads a jump. Intentionally STRICTER than the
        // other modes' `lead_in || running` gate: a Jump `down` while grounded
        // launches immediately, so queuing count-in taps would auto-jump the
        // instant the run begins. A button still HELD as the run goes live is
        // re-armed separately at the lead-in → running edge (see tick()).
        () => this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as {
        __beatHeavenJumpTestHook?: JumpGameSessionTestHook;
      }).__beatHeavenJumpTestHook = this.buildTestHook();
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, { speedRamp: ramp });
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  async startNextLoop(): Promise<void> {
    if (this.destroyed || this.status !== 'awaiting_next_loop') {
      return;
    }
    this.loopState.advanceLoop();
    resetJumpStateForNextLoop(this.state, this.chart);
    this.lastBeat = 0;
    this.comboMilestonesHit.clear();
    this.loopCapturedInputs.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);
    this.flashes.length = 0;
    this.shockwaveSpecs.length = 0;
    this.particleEmissions.length = 0;
    this.renderer.clearTrail();
    this.inputSource.rehomeForLoopStart(0);

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    this.renderer.resetAll();
    await this.audio.stop();
    delete (window as unknown as {
      __beatHeavenJumpTestHook?: JumpGameSessionTestHook;
    }).__beatHeavenJumpTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): JumpSessionStatus {
    return this.status;
  }

  getCapturedReplay(): JumpReplayInputs {
    return {
      loops: this.loopCapturedInputs.map((inputs) => ({ inputs: inputs.slice() })),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /** Anti-cheat timing stream for this run (null if nothing captured). */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }

  private setStatus(s: JumpSessionStatus): void {
    if (this.status === s) {
      return;
    }
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') {
      return;
    }

    const currentBeat = this.audio.getCurrentBeat();
    if (!Number.isFinite(currentBeat)) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }
    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      this.tickBeatPulse(currentBeat, dt);
      this.drawFrame(currentBeat, leadInBeat);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
      // A button held all the way through the count-in launches the cube the
      // instant the run goes live (then auto-hops while held); a tap released
      // during the count-in stays inert. Loops 2+ already engage a held button
      // via startNextLoop's rehome, but the first run can't — the hold is
      // acquired DURING the count-in, after start() ran — so sample it here at
      // the lead-in → running edge. The synthetic `down` is captured + replayed
      // like any input, so the server re-sim agrees.
      if (this.loopState.getLoopIndex() === 0) {
        this.inputSource.rehomeForLoopStart(0);
      }
    }

    this.tickShake(dt);
    this.tickBeatPulse(currentBeat, dt);
    this.tickEdgeImpulse(dt);
    this.tickComboEnergy(dt);

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    const rawInputs = this.inputSource.poll(cappedTarget);
    // Inputs are only captured once the run is live (beat >= 0). Clamp each into
    // this frame's steppable [lastBeat, cappedTarget] window so a press that
    // landed a hair outside the frame's reach (rAF stall) or the synthetic
    // loop-restart `down` at beat 0 still resolves on a valid in-window beat.
    const clampedInputs: JumpEngineInput[] = rawInputs.map((inp) => ({
      beat: Math.max(this.lastBeat, Math.min(Math.max(inp.beat, 0), cappedTarget)),
      type: inp.type,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({ beat: inp.beat, type: inp.type });
    }

    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
    });
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
    }

    this.detectAndFireMidRunMilestones();
    this.tickDisplayedScore(dt);

    this.drawFrame(this.state.beat, null);

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private tickShake(dt: number): void {
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
      if (this.shake.amount < 0.01) {
        this.shake.biasY = undefined;
      }
    }
  }

  private tickBeatPulse(currentBeat: number, dt: number): void {
    const beatFrac = ((currentBeat % 1) + 1) % 1;
    if (beatFrac < 0.08) {
      this.currentBeatPulse = 1;
    } else {
      const beatsPerSec = this.chart.bpm / 60;
      const decayPerSec = beatsPerSec * 1.6;
      this.currentBeatPulse = Math.max(0, this.currentBeatPulse - decayPerSec * dt);
    }
  }

  /** Ease the signed cube deformation impulse back toward 0 from whichever side it fired. */
  private tickEdgeImpulse(dt: number): void {
    if (this.edgeImpulse === 0) {
      return;
    }
    const decay = CUBE_EDGE_IMPULSE_DECAY * dt;
    if (this.edgeImpulse > 0) {
      this.edgeImpulse = Math.max(0, this.edgeImpulse - decay);
    } else {
      this.edgeImpulse = Math.min(0, this.edgeImpulse + decay);
    }
  }

  /**
   * Ease the render-only combo energy toward a target derived from the live
   * combo (saturating at {@link COMBO_ENERGY_FULL_AT}). Smooth exponential
   * approach so the glow / trail / bloom escalate gracefully rather than
   * snapping. Purely cosmetic — the engine never reads this back.
   */
  private tickComboEnergy(dt: number): void {
    const target = Math.min(1, this.state.combo / COMBO_ENERGY_FULL_AT);
    const k = 1 - Math.exp(-COMBO_ENERGY_APPROACH_RATE * dt);
    this.comboEnergy += (target - this.comboEnergy) * k;
  }

  private tickDisplayedScore(dt: number): void {
    const target = this.state.score;
    if (this.displayedScore < target) {
      const k = 1 - Math.exp(-SCORE_DISPLAY_LERP_RATE * dt);
      this.displayedScore += (target - this.displayedScore) * k;
      if (target - this.displayedScore < 0.5) {
        this.displayedScore = target;
      }
    } else if (this.displayedScore > target) {
      this.displayedScore = target;
    }
  }

  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) {
      return;
    }
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.spawnMilestoneFx(completionMilestone);
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        return;
      }
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!Number.isFinite(songTime)) {
      return null;
    }
    if (songTime >= 0) {
      return null;
    }
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }

  private attachResizeObserver(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(this.opts.canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null): void {
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      beat,
      width: this.cachedRect.width,
      height: this.cachedRect.height,
      pressing: this.inputSource.isPressing(),
      displayedScore: this.displayedScore,
      countdown: leadInBeat,
      floatingTexts: this.floatingTexts,
      scorePops: this.scorePops,
      particleEmissions: this.particleEmissions,
      shake: this.shake,
      flashes: this.flashes,
      shockwaveSpecs: this.shockwaveSpecs,
      beatPulse: this.currentBeatPulse,
      comboEnergy: this.comboEnergy,
      edgeImpulse: this.edgeImpulse,
      keyLabel: this.opts.keyLabel ?? 'SPACE',
    });
  }

  private spawnFloatingText(
    text: string,
    x: number,
    y: number,
    colour: string,
    opts: { big?: boolean; vy?: number; scale?: number } = {},
  ): void {
    const big = opts.big ?? false;
    const life = big ? FLOATING_TEXT_LIFE_BIG : FLOATING_TEXT_LIFE;
    const jitterX = big ? 0 : (Math.random() - 0.5) * 22;
    this.floatingTexts.push({
      text,
      x: x + jitterX,
      y: y - 28,
      colour,
      vy: opts.vy ?? -55,
      scale: opts.scale ?? 1,
      life,
      maxLife: life,
      big,
    });
  }

  private spawnScorePop(
    anchorX: number,
    anchorY: number,
    scoreDelta: number,
    verdict: 'perfect' | 'good',
  ): void {
    if (scoreDelta <= 0) {
      return;
    }
    const big = scoreDelta >= 500;
    const target = this.renderer.getScoreHudAnchor();
    this.scorePops.push({
      text: `+${scoreDelta}`,
      startX: anchorX,
      startY: anchorY,
      targetX: target.x,
      targetY: target.y,
      colour: verdict === 'perfect' ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good,
      big,
      life: SCORE_POP_LIFE,
      maxLife: SCORE_POP_LIFE,
    });
  }

  private addShake(amount: number, biasY?: number): void {
    // The strongest active shake owns the decay + bias; a weaker call (e.g. the
    // per-landing thud that fires constantly under auto-hop) must not slacken a
    // punchier celebratory shake that is still decaying.
    if (amount < this.shake.amount) {
      return;
    }
    this.shake.amount = amount;
    this.shake.decay = amount * 6;
    if (biasY !== undefined) {
      this.shake.biasY = biasY;
    }
  }

  private pushShockwave(
    x: number,
    y: number,
    colour: string,
    opts: {
      startR?: number;
      endR?: number;
      life?: number;
      alpha?: number;
      /** <1 flattens the ring into a ground-hugging ripple ellipse (default circular). */
      flatten?: number;
    } = {},
  ): void {
    this.shockwaveSpecs.push({
      x,
      y,
      startR: opts.startR ?? 24,
      endR: opts.endR ?? 95,
      life: opts.life ?? 0.38,
      thickness: 3,
      colour,
      alpha: opts.alpha ?? 0.75,
      flatten: opts.flatten,
    });
  }

  private spawnMilestoneFx(kind: MilestoneKind): void {
    const isStar = milestoneIsStar(kind);
    const tier = milestoneTier(kind);
    const colour = isStar ? MILESTONE_COLOUR : '#ffb13b';
    const anchor = this.renderer.getPlayerAnchor(this.state);
    this.particleEmissions.push({
      type: 'milestone',
      x: anchor.x,
      y: anchor.y,
      colour,
      tier,
    });
    const glyph = isStar ? '\u2605' : '\u{1F3C6}';
    this.spawnFloatingText(`${glyph} x${tier}`, anchor.x, anchor.y, colour, {
      big: true,
      vy: -90,
      scale: tier === 3 ? 1.4 : 1.15,
    });
    this.addShake(tier === 3 ? 14 : 6 + tier * 2);
    this.flashes.push({
      type: 'milestone',
      t: 0.35,
      intensity: tier === 3 ? 1.4 : 1,
      colour,
    });
    this.pushShockwave(anchor.x, anchor.y, colour, {
      startR: 32,
      endR: tier === 3 ? 140 : 110,
      life: 0.5,
      alpha: 0.85,
    });
    this.audio.playMilestoneFanfare(tier);
    if (kind === 'star_3') {
      this.audio.duckMusic(0.12, 350);
      this.audio.playVictorySting();
    }
  }

  private handleEvent(ev: JumpEngineEvent): void {
    if (ev.type === 'obstacle_passed') {
      this.handleObstaclePassed(ev);
    } else if (ev.type === 'jump') {
      // Launch pop: stretch the cube upward (−1 per the engine's +down convention).
      this.edgeImpulse = -1;
      const anchor = this.renderer.getPlayerAnchor(this.state);
      this.particleEmissions.push({
        type: 'jump',
        x: anchor.x,
        y: anchor.y + this.chart.playerSize * 0.4,
        colour: this.chart.palette.player,
      });
      this.audio.scheduleFlip();
    } else if (ev.type === 'land') {
      // Landing splat: squash the cube down (+1) and kick a ground ripple.
      this.edgeImpulse = 1;
      const base = this.renderer.getCubeBaseAnchor(this.chart, this.state);
      this.particleEmissions.push({
        type: 'land',
        x: base.x,
        y: base.y,
        colour: this.chart.palette.player,
      });
      // A flattened ripple spreads along the surface so the touchdown lands with weight.
      this.pushShockwave(base.x, base.y, this.chart.palette.player, {
        startR: 10,
        endR: LAND_RIPPLE_END_R,
        life: LAND_RIPPLE_LIFE,
        alpha: LAND_RIPPLE_ALPHA,
        flatten: GROUND_RIPPLE_FLATTEN,
      });
      // Subtle downward punch so touchdowns feel grounded (render-only shake).
      this.addShake(LAND_SHAKE_AMOUNT, 1);
      this.audio.scheduleMashTap();
    } else if (ev.type === 'death') {
      this.handleDeath();
    } else if (ev.type === 'edge') {
      // Press/release toggles the hold (auto-hop) state; the jump itself
      // emits a separate `jump` event with its own SFX, so the edge needs
      // no extra sound — only the renderer's pressing visual reacts.
    } else if (ev.type === 'completed') {
      // The loop wrap is handled by handleLoopComplete(); the event is
      // emitted purely so analytics + replay logs see the boundary.
    }
  }

  private handleObstaclePassed(
    ev: Extract<JumpEngineEvent, { type: 'obstacle_passed' }>,
  ): void {
    const anchor = this.renderer.getObstacleAnchor(
      this.chart,
      this.state,
      ev.beat,
      this.state.beat,
    );
    const verdict = ev.payload.verdict;
    const isPerfect = verdict === 'perfect';
    const verdictColour = isPerfect ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good;
    // A close call = a survival with almost no clearance. Spike-hitbox
    // forgiveness lets the cube clip a tip and live (clearance can even dip
    // slightly negative), so these grade `good`; celebrate the near-miss with
    // its own "CLOSE!" beat rather than a plain GOOD. A `perfect` always clears
    // by >= JUMP_PERFECT_MIN_CLEARANCE (well above CLOSE_CALL_CLEARANCE), so it
    // is never a close call.
    const lastResolution = this.state.resolutions[this.state.resolutions.length - 1];
    const isCloseCall =
      !isPerfect &&
      lastResolution !== undefined &&
      lastResolution.obstacleId === ev.payload.obstacleId &&
      lastResolution.clearance < CLOSE_CALL_CLEARANCE;
    this.particleEmissions.push({
      type: 'obstacle_pass',
      x: anchor.x,
      y: anchor.y,
      colour: verdictColour,
      strength: isPerfect ? PASS_PARTICLE_STRENGTH_PERFECT : PASS_PARTICLE_STRENGTH_GOOD,
    });
    this.pushShockwave(anchor.x, anchor.y, verdictColour, {
      startR: 26,
      endR: 100,
      life: 0.4,
      alpha: 0.7,
    });
    if (isPerfect) {
      // A second, faster inner ring + a small punch give a PERFECT the layered
      // "crack" a GOOD lacks, so a clean clear reads as clearly more satisfying.
      this.pushShockwave(anchor.x, anchor.y, verdictColour, {
        startR: 12,
        endR: 62,
        life: 0.26,
        alpha: 0.9,
      });
      this.addShake(PASS_SHAKE_PERFECT);
    }
    this.flashes.push({
      type: isPerfect ? 'pass' : 'good',
      t: 0.16,
      intensity: isPerfect ? PASS_FLASH_INTENSITY_PERFECT : PASS_FLASH_INTENSITY_GOOD,
    });
    this.renderer.requestHitStop(isPerfect ? HIT_STOP_MS_PASS_PERFECT : HIT_STOP_MS_PASS_GOOD);
    this.spawnScorePop(anchor.x, anchor.y, ev.payload.scoreDelta, verdict);
    const verdictTextAnchor = this.renderer.getVerdictTextAnchor(this.state);
    // A close call shows "CLOSE!" below instead, so skip the plain verdict text.
    if (!isCloseCall) {
      this.spawnFloatingText(
        isPerfect ? 'PERFECT!' : 'GOOD',
        verdictTextAnchor.x,
        verdictTextAnchor.y,
        verdictColour,
        { scale: isPerfect ? 1.05 : 0.95 },
      );
    }
    // comboNote walks the clear chime up a pentatonic scale as the streak grows
    // (the same melodic-reward pattern Circles / Piano / Guitar use). Audio-only.
    this.audio.scheduleHitFeedback(verdict, undefined, { comboNote: this.state.combo });

    if (isCloseCall) {
      this.particleEmissions.push({
        type: 'obstacle_pass',
        x: anchor.x,
        y: anchor.y,
        colour: CLOSE_CALL_COLOUR,
      });
      this.pushShockwave(anchor.x, anchor.y, CLOSE_CALL_COLOUR, {
        startR: 18,
        endR: 80,
        life: 0.32,
        alpha: 0.9,
      });
      this.spawnFloatingText('CLOSE!', verdictTextAnchor.x, verdictTextAnchor.y, CLOSE_CALL_COLOUR, {
        vy: -110,
        scale: 0.9,
      });
      this.flashes.push({ type: 'close', t: 0.2, intensity: 0.85 });
      this.renderer.requestHitStop(HIT_STOP_MS_CLOSE);
      this.addShake(5);
    }

    for (const milestone of COMBO_MILESTONES) {
      if (this.state.combo === milestone && !this.comboMilestonesHit.has(milestone)) {
        this.comboMilestonesHit.add(milestone);
        const playerAnchor = this.renderer.getPlayerAnchor(this.state);
        this.spawnFloatingText(`x${milestone} COMBO!`, playerAnchor.x, playerAnchor.y, '#ff2bd6', {
          big: true,
          vy: -80,
        });
        this.pushShockwave(playerAnchor.x, playerAnchor.y, '#ff2bd6', {
          startR: 40,
          endR: 130,
          life: 0.55,
          alpha: 0.8,
        });
        this.flashes.push({ type: 'close', t: 0.28, intensity: 1.1 });
        this.audio.scheduleComboMilestone();
        this.addShake(3);
      }
    }
  }

  private handleDeath(): void {
    const anchor = this.renderer.getPlayerAnchor(this.state);
    this.particleEmissions.push({
      type: 'death',
      x: anchor.x,
      y: anchor.y,
      colour: DEATH_COLOUR,
    });
    this.pushShockwave(anchor.x, anchor.y, DEATH_COLOUR, {
      startR: 36,
      endR: 120,
      life: 0.45,
      alpha: 0.9,
    });
    this.spawnFloatingText('GAME OVER', anchor.x, anchor.y, DEATH_COLOUR, {
      big: true,
      vy: -30,
    });
    this.addShake(14, 1);
    this.flashes.push({ type: 'death', t: 0.55, intensity: 1.2 });
    this.renderer.requestHitStop(HIT_STOP_MS_DEATH);
    this.audio.playDeath();
    this.audio.duckMusicPulse(0.5, 40, 280);
  }

  private finishSession(status: JumpSessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      oks: this.state.oks,
      misses: this.state.misses,
      accuracy: computeAccuracy(
        this.state.perfects,
        this.state.goods,
        this.state.oks,
        this.state.misses,
      ),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    this.audio.stopMusic();
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): JumpGameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
    };
  }
}

export interface JumpGameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => JumpSessionStatus;
  getState: () => JumpEngineState;
  getBeat: () => number;
  getEvents: () => JumpEngineEvent[];
  isAudioStarted: () => boolean;
}
